import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ProductVariant, MerchantProduct, Product } from "../../../../types/ProductListing";
import { calculateStockFromAllocation } from "../../../../utils/stockCalculations";

// Helper function to get merchant product display value and clickability with ellipsis
const getMerchantProductDisplayValue = (
  merchantProduct: MerchantProduct | undefined, 
  maxLength: number = 25
): { value: string, isClickable: boolean, fullValue: string, needsTruncation: boolean } => {
  if (!merchantProduct) {
    return { 
      value: "(N/A)", 
      isClickable: false, 
      fullValue: "(N/A)", 
      needsTruncation: false 
    };
  }
  
  const { sku, name, id } = merchantProduct;
  let fullValue = "";
  
  // Priority order: sku > name > "Merchant ID: {id}"
  if (sku) {
    fullValue = sku;
  } else if (name) {
    fullValue = name;
  } else if (id) {
    fullValue = `Merchant ID: ${id}`;
  } else {
    return { 
      value: "(N/A)", 
      isClickable: false, 
      fullValue: "(N/A)", 
      needsTruncation: false 
    };
  }
  
  const needsTruncation = fullValue.length > maxLength;
  const displayValue = needsTruncation 
    ? fullValue.substring(0, maxLength - 3) + "..."
    : fullValue;
  
  return { 
    value: displayValue, 
    isClickable: true, 
    fullValue: fullValue, 
    needsTruncation: needsTruncation 
  };
};

interface ProductVariantRowProps {
  variant: ProductVariant;
  parentProduct: Product;
  parentProductId: string;
  onRowClick?: (variant: ProductVariant) => void;
  onStockUpdate?: (product: Product) => void;
  onPriceUpdate?: (product: Product) => void;
  className?: string;
}

function ProductVariantRow({
  variant,
  parentProduct,
  parentProductId,
  onRowClick,
  onStockUpdate,
  onPriceUpdate,
  className = "",
}: ProductVariantRowProps) {
  const { t } = useTranslation();

  const getStockStatusClass = (stock: number) => {
    if (stock === 0) return "tw-text-red-600 tw-font-bold";
    if (stock <= 10) return "tw-text-amber-600 tw-font-bold";
    return "tw-text-gray-800";
  };

  const formatPrice = (price: number, currency: string = "IDR") => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getResponsiveClass = (responsive: string) => {
    switch (responsive) {
      case "md":
        return "tw-hidden md:tw-table-cell";
      case "lg":
        return "tw-hidden lg:tw-table-cell";
      default:
        return "";
    }
  };

  const formatVariantAttributes = () => {
    if (!variant.tierVariation || variant.tierVariation.length === 0) {
      return "No attributes";
    }

    return variant.tierVariation
      .map((attr) =>
        Object.entries(attr)
          .map(([key, value]) => `${key}: ${value}`)
          .join(", ")
      )
      .join(" | ");
  };

  return (
    <tr
      className={clsx(
        "product-variant-row tw-bg-gray-50/50",
        onRowClick && "tw-cursor-pointer",
        className
      )}
      onClick={() => onRowClick?.(variant)}
      data-testid={`variant-row-${variant.id}`}
    >
      {/* Product column - with indentation */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-flex tw-items-start tw-gap-3 tw-ml-6">
          {variant.photo ? (
            <img
              src={variant.photo}
              alt={`Variant ${variant.id}`}
              className="tw-h-9 tw-w-9 tw-rounded tw-object-cover tw-shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="tw-h-9 tw-w-9 tw-rounded tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <span className="material-symbols-outlined tw-text-gray-400 tw-text-sm">
                inventory_2
              </span>
            </div>
          )}
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <p className="tw-font-medium tw-text-gray-700 tw-text-sm">
              {formatVariantAttributes()}
            </p>
            <p className="tw-text-xs tw-text-gray-500">{variant.sku}</p>
          </div>
        </div>
      </td>

      {/* Merchant Product column */}
      <td
        className={clsx(
          "tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm",
          getResponsiveClass("md")
        )}
      >
        {(() => {
          const { value, isClickable, fullValue, needsTruncation } = getMerchantProductDisplayValue(variant.merchantProduct);
          
          if (isClickable) {
            return (
              <div className="tw-relative custom-tooltip">
                <a 
                  className="tw-text-blue-600 hover:tw-underline tw-cursor-pointer" 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // TODO: Add click handler for variant merchant product
                    console.log('Variant merchant product clicked:', variant.merchantProduct);
                  }}
                >
                  {value}
                </a>
                {needsTruncation && (
                  <span className="custom-tooltiptext">
                    {fullValue}
                  </span>
                )}
              </div>
            );
          } else {
            return <span className="tw-text-gray-500">{value}</span>;
          }
        })()}
      </td>

      {/* Store column - empty for variants */}
      <td
        className={clsx(
          "tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm",
          getResponsiveClass("md")
        )}
      ></td>

      {/* Stock column */}
      <td
        className={clsx(
          "tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm tw-text-right",
          getResponsiveClass("md")
        )}
      >
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <span className={getStockStatusClass(calculateStockFromAllocation(variant.inventoryAllocation))}>
            {calculateStockFromAllocation(variant.inventoryAllocation).toLocaleString()}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStockUpdate?.(parentProduct);
            }}
            className="tw-text-gray-400 hover:tw-text-primary custom-tooltip"
          >
            <span className="material-symbols-outlined tw-text-base filled">
              calculate
            </span>
            <span className="custom-tooltiptext">Edit stock by warehouse</span>
          </button>
        </div>
      </td>

      {/* Price column */}
      <td
        className={clsx(
          "tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm tw-text-right",
          getResponsiveClass("md")
        )}
      >
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <div className="tw-flex tw-flex-col tw-text-right">
            <span className="tw-font-medium tw-text-gray-800">
              {formatPrice(variant.price.current, variant.price.currency)}
            </span>
            {variant.price.original > variant.price.current && (
              <span className="tw-text-xs tw-text-gray-500 tw-line-through">
                {formatPrice(variant.price.original, variant.price.currency)}
              </span>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPriceUpdate?.(parentProduct);
            }}
            className="tw-text-gray-400 hover:tw-text-primary custom-tooltip"
          >
            <span className="material-symbols-outlined tw-text-base filled">
              payments
            </span>
            <span className="custom-tooltiptext">
              Edit product or variant pricing
            </span>
          </button>
        </div>
      </td>

      {/* Sold (30d) column */}
      <td
        className={clsx(
          "tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm tw-text-right tw-text-gray-700",
          getResponsiveClass("lg")
        )}
      >
        {variant.salesMetrics.salesQty30d.toLocaleString()}
      </td>

      {/* Last Sync column - empty for variants */}
      <td className={clsx(getResponsiveClass("lg"))}></td>

      {/* Product Quality column - empty for variants */}
      <td
        className={clsx(
          "tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top",
          getResponsiveClass("lg")
        )}
      ></td>

      {/* Actions column - empty for variants */}
      <td className="tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium"></td>
    </tr>
  );
}

export default ProductVariantRow;
export type { ProductVariantRowProps };
